import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { TableCard } from '@/components/common/table/TableCard'
import { HealthInsuranceOverviewDataTable } from './HealthInsuranceOverviewDataTable'
import { useHealthInsuranceConfigs } from '../hooks/useHealthInsuranceConfigs.ts'

interface Props {
  module: ServiceModuleModel
  onRowClick?: (employee: EmployeeUserResponse) => void
  onEdit?: (employee: EmployeeUserResponse) => void
}

function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

export function HealthInsuranceOverviewTable({ module, onRowClick, onEdit }: Props) {
  const { t } = useTranslation(['service-modules-insurance', 'filters'])
  const [search, setSearch] = useState<string | undefined>()

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const { healthConfigsByEmployeeId } = useHealthInsuranceConfigs(module.id)

  const filteredEmployees = useMemo(() => {
    const query = search?.trim().toLowerCase()
    if (!query) return employees
    return employees.filter((e) =>
      `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(query),
    )
  }, [employees, search])

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <FilterRow>
        <FilterSearch
          search={search}
          onChange={setSearch}
          useDebounce={false}
          searchLabel={t('searchbar.searchEmployees', { ns: 'filters' })}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <HealthInsuranceOverviewDataTable
            employees={filteredEmployees}
            configsByEmployeeId={healthConfigsByEmployeeId}
            isLoading={employeesQuery.isLoading}
            isError={employeesQuery.isError}
            onRowClick={onRowClick}
            onEdit={onEdit}
          />
        </div>
      </TableCard>
    </div>
  )
}
